import { useEffect } from 'react'
import { trackPanelView } from '@/lib/analytics/gtm'

type PanelViewOptions = {
  panelId: string
  panelName: string
  panelIndex: number
  threshold?: number
}

const FALLBACK_THRESHOLD = 0.5

export function usePanelViewTracking({
  panelId,
  panelName,
  panelIndex,
  threshold = FALLBACK_THRESHOLD,
}: PanelViewOptions) {
  useEffect(() => {
    if (typeof window === 'undefined') {
      return
    }

    const element = document.getElementById(panelId)
    if (!element) {
      return
    }

    // `sessionStorage` access can throw in some privacy modes; treat as "no storage" in that case.
    let storage: Storage | null = null
    try {
      storage = sessionStorage
    } catch {
      storage = null
    }

    const storageKey = `panel_view:${panelId}`
    if (storage?.getItem(storageKey)) {
      return
    }

    if (!('IntersectionObserver' in window)) {
      storage?.setItem(storageKey, '1')
      trackPanelView(panelId, panelName, panelIndex)
      return
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0]
        if (!entry) {
          return
        }

        if (entry.isIntersecting) {
          storage?.setItem(storageKey, '1')
          trackPanelView(panelId, panelName, panelIndex)
          observer.disconnect()
        }
      },
      { threshold }
    )

    observer.observe(element)

    return () => observer.disconnect()
  }, [panelId, panelIndex, panelName, threshold])
}
